import camelize from 'camelize';
import { baseCreateApi } from 'services';

export const meetingsApi = baseCreateApi.injectEndpoints({
  overrideExisting: true,
  endpoints: builder => ({
    getMeetingChapters: builder.query({
      providesTags: ['MeetingChapters'],
      query: ({ meetingUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/chapters/`,
        method: 'GET'
      }),
      transformResponse: response => camelize(response)
    }),
    updateMeetingChapter: builder.mutation({
      invalidatesTags: ['MeetingChapters'],
      query: ({ meetingUuid, chapterUuid, payload }) => ({
        url: `/api/v1/meeting/${meetingUuid}/chapters/${chapterUuid}/`,
        method: 'PATCH',
        data: payload
      }),
      transformResponse: response => camelize(response)
    }),
    createMeetingChapter: builder.mutation({
      invalidatesTags: ['MeetingChapters'],
      query: ({ meetingUuid, payload }) => ({
        url: `/api/v1/meeting/${meetingUuid}/chapters/`,
        method: 'POST',
        data: payload
      }),
      transformResponse: response => camelize(response)
    }),
    createMeetingChaptersWithAi: builder.mutation({
      invalidatesTags: ['MeetingChapters'],
      query: ({ meetingUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/chapters/generate/`,
        method: 'POST'
      }),
      transformResponse: response => camelize(response)
    }),
    deleteMeetingChapter: builder.mutation({
      invalidatesTags: ['MeetingChapters'],
      query: ({ meetingUuid, chapterUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/chapters/${chapterUuid}/`,
        method: 'DELETE'
      }),
      transformResponse: response => camelize(response)
    }),
    getMeetingFollowUpEmail: builder.mutation({
      query: ({ meetingUuid, templateUuid }) => ({
        url: templateUuid
          ? `/api/v1/meeting/${meetingUuid}/follow_up/${templateUuid}/`
          : `/api/v1/meeting/${meetingUuid}/follow_up/`,
        method: 'POST'
      }),
      transformResponse: response => camelize(response)
    }),
    getMeetingFollowUpAutoTemplate: builder.query({
      providesTags: (result, error, { meetingUuid }) => [
        { type: 'MeetingFollowUpAutoTemplate', id: meetingUuid }
      ],
      query: ({ meetingUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/follow_up/auto_template/`,
        method: 'GET'
      }),
      transformResponse: response => {
        const normalizedResponse = camelize(response);

        return {
          template: normalizedResponse?.template || null,
          isSequence: Boolean(normalizedResponse?.isSequence)
        };
      }
    }),
    updateMeetingFollowUpTemplate: builder.mutation({
      query: ({ meetingUuid, templateUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/follow_up/${templateUuid}/`,
        method: 'POST'
      }),
      transformResponse: response => camelize(response)
    }),
    previewMeetingFollowUpTemplate: builder.mutation({
      query: ({ meetingUuid, prompt }) => ({
        url: `/api/v1/meeting/${meetingUuid}/follow_up/preview_template/`,
        method: 'POST',
        data: { prompt }
      }),
      transformResponse: response => camelize(response)
    }),
    getMeetingSequenceEmail: builder.query({
      providesTags: ['MeetingSequenceEmail'],
      query: ({ meetingUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/follow_up/sequence/`,
        method: 'POST'
      }),
      transformResponse: response => camelize(response)
    }),
    getMeetingTypes: builder.query({
      providesTags: ['MeetingTypes'],
      query: () => ({
        url: '/api/v1/meetingtype/',
        method: 'GET'
      }),
      transformResponse: response => camelize(response)
    }),
    getMeetingInsights: builder.query({
      providesTags: ['MeetingInsights'],
      query: ({ meetingUuid }) => ({
        url: `/api/v2/insights/${meetingUuid}/`,
        method: 'GET'
      }),
      transformResponse: response => camelize(response)
    }),
    getMeetingSpeakingInsights: builder.query({
      providesTags: ['MeetingSpeakingInsights'],
      query: ({ meetingUuid }) => ({
        url: `/api/v2/insights/speaking_insights/${meetingUuid}/`,
        method: 'GET'
      }),
      transformResponse: response => camelize(response)
    }),
    getMeetingAssociatedTemplate: builder.query({
      providesTags: ['MeetingAssociatedTemplate'],
      query: ({ meetingUuid, meetingType }) => ({
        url: `/api/v1/meeting/${meetingUuid}/collabnotes/associated-template/`,
        method: 'GET'
      }),
      transformResponse: response => camelize(response?.[0])
    }),
    changeMeetingAssociatedTemplate: builder.mutation({
      invalidatesTags: ['MeetingAssociatedTemplate'],
      query: ({ meetingUuid, templateUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/collabnotes/change_associated_template/`,
        method: 'POST',
        data: {
          template_uuid: templateUuid
        }
      }),
      transformResponse: response => camelize(response)
    }),
    getMeetingCollabNotesSnapshotHistory: builder.query({
      providesTags: ['MeetingCollabNotesSnapshotHistory'],
      query: ({ meetingUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/collabnotes/snapshot_history/`,
        method: 'GET'
      }),
      transformResponse: response => camelize(response)
    }),
    getMeetingCollabNotesSnapshot: builder.query({
      providesTags: ['MeetingCollabNotesSnapshot'],
      query: ({ meetingUuid, snapshotUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/collabnotes/snapshot_history/?snapshot_uuid=${snapshotUuid}`,
        method: 'GET'
      }),
      transformResponse: response => camelize(response)
    }),
    regenerateMeetingNotes: builder.mutation({
      invalidatesTags: [
        'MeetingCollabNotesSnapshotHistory',
        'MeetingAssociatedTemplate',
        'RegenerateNotesLimits',
        'MeetingCollabNotesPreviewHistory'
      ],
      query: ({ meetingUuid, templateUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/collabnotes/generate_note/`,
        method: 'POST',
        data: {
          template_uuid: templateUuid || null
        }
      }),
      transformResponse: response => camelize(response)
    }),
    saveNotesSnapshot: builder.mutation({
      invalidatesTags: ['MeetingCollabNotesSnapshotHistory'],
      query: ({ meetingUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/collabnotes/create_snapshot/`,
        method: 'POST'
      }),
      transformResponse: response => camelize(response)
    }),
    getRegenerateNotesLimits: builder.query({
      providesTags: ['RegenerateNotesLimits'],
      query: ({ meetingUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/collabnotes/regeneration_limits/`,
        method: 'GET'
      }),
      transformResponse: response => camelize(response)
    }),
    acceptNotePreview: builder.mutation({
      invalidatesTags: ['MeetingCollabNotesPreviewHistory'],
      query: ({ meetingUuid, previewUuid }) => ({
        url: `/api/v1/meeting/${meetingUuid}/collabnotes/accept_note_preview/`,
        method: 'POST',
        data: { preview_uuid: previewUuid }
      }),
      transformResponse: response => camelize(response)
    }),
    getMeetingMethodology: builder.query({
      providesTags: ['MeetingMethodology'],
      query: ({ meetingUuid }) => ({
        url: `/api/v1/playbook_setting/meeting/${meetingUuid}/`,
        method: 'GET',
        options: { ignoreErrorNotification: true }
      }),
      serializeQueryArgs: ({ queryArgs }) => queryArgs.meetingUuid,
      transformResponse: response => camelize(response),
      async onQueryStarted({ meetingUuid }, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          // Silently handle 404 errors (i.e. no associated playbook for the meeting)
          if (error?.error?.status === 404) {
            return;
          }
          // Re-throw other errors
          throw error;
        }
      }
    }),
    getRiskAnalysis: builder.query({
      providesTags: ['AIReports'],
      query: ({ meetingUuid, reportType }) => ({
        url: `/api/v1/meeting_reports/${meetingUuid}/?report_type=${reportType}`,
        method: 'GET',
        options: { ignoreErrorNotification: true }
      }),
      transformResponse: response => camelize(response)
    }),
    getMeetingLastCompletedInSeries: builder.query({
      // Optional `?count=N` selects the N most recent completed instances (defaults
      // to 1 server-side, capped at 10). Frontend only needs the latest one today so
      // the param is omitted; revisit if a future surface wants more than one.
      // Example: GET /api/v2/meetings/{meetingUuid}/last_completed_in_series/?count=1
      query: ({ meetingUuid }) => ({
        url: `/api/v2/meetings/${meetingUuid}/last_completed_in_series/`,
        method: 'GET',
        options: { ignoreErrorNotification: true }
      }),
      // Backend returns `{ meetings: [{ uuid, name, hosted_by, date }] }` — flatten to
      // the first meeting (or null) so consumers in Copilot / MeetingAskAvomaButton
      // can keep reading `.uuid` directly without knowing about the envelope.
      transformResponse: response => {
        if (!response) return null;
        const camelized = camelize(response);
        const meetings = camelized?.meetings;
        if (Array.isArray(meetings) && meetings.length > 0) {
          return meetings[0];
        }
        return null;
      }
    })
  })
});

export const {
  useGetMeetingChaptersQuery,
  useCreateMeetingChapterMutation,
  useCreateMeetingChaptersWithAiMutation,
  useDeleteMeetingChapterMutation,
  useUpdateMeetingChapterMutation,
  useGetMeetingFollowUpAutoTemplateQuery,
  useGetMeetingFollowUpEmailMutation,
  useUpdateMeetingFollowUpTemplateMutation,
  usePreviewMeetingFollowUpTemplateMutation,
  useLazyGetMeetingSequenceEmailQuery,
  useGetMeetingInsightsQuery,
  useGetMeetingSpeakingInsightsQuery,
  useLazyGetMeetingSpeakingInsightsQuery,
  useGetMeetingAssociatedTemplateQuery,
  useChangeMeetingAssociatedTemplateMutation,
  useGetMeetingCollabNotesSnapshotHistoryQuery,
  useGetMeetingCollabNotesSnapshotQuery,
  useRegenerateMeetingNotesMutation,
  useGetRegenerateNotesLimitsQuery,
  useSaveNotesSnapshotMutation,
  useAcceptNotePreviewMutation,
  useGetMeetingMethodologyQuery,
  useGetRiskAnalysisQuery,
  useGetMeetingLastCompletedInSeriesQuery,
  useLazyGetMeetingLastCompletedInSeriesQuery
} = meetingsApi;
